import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isEnterprise } from '../services/subscription';
import { getMyEnterpriseUsage, highestMilestone } from '../services/enterpriseUsageService';
import { notifyInfo } from '../services/notify';

/**
 * EnterprisePoolNotifier
 *
 * For enterprise teachers, checks the org's shared-pool usage on load and fires a
 * ONE-TIME toast when it crosses a new 50 / 75 / 90 / 100% milestone. The
 * last-notified milestone is remembered per org + period in localStorage, so a
 * teacher sees each threshold once. Renders nothing.
 *
 * Per-teacher usage stays private — this only surfaces the org-wide percentage.
 */
export const EnterprisePoolNotifier: React.FC = () => {
  const { userProfile } = useAuth();
  const enterprise = isEnterprise(userProfile);

  useEffect(() => {
    if (!enterprise) return;
    let cancelled = false;
    getMyEnterpriseUsage()
      .then((u) => {
        if (cancelled || !u.enterprise || !u.poolLimit || typeof u.orgPct !== 'number') return;
        const milestone = highestMilestone(u.orgPct);
        if (milestone == null) return;
        const key = `poolMilestone_${u.orgName || 'org'}_${u.periodKey || ''}`;
        const seen = Number(localStorage.getItem(key) || '0');
        if (milestone <= seen) return;
        localStorage.setItem(key, String(milestone));
        const msg =
          milestone >= 100
            ? `Your school (${u.orgName}) has used its full monthly AI credit pool.`
            : `Your school (${u.orgName}) has used ${milestone}% of its monthly AI credit pool.`;
        notifyInfo(msg, { id: `pool-${milestone}`, duration: 8000 });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enterprise]);

  return null;
};
